import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ShoppingBag } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageLoading, EmptyState, PageHeader, fmtDate } from "@/components/shared";
import api from "@/lib/api";

const prettify = (s) => (s || "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
const SERVICE_STATUSES = ["CREATED", "CHECKOUT_PENDING", "PAID", "IN_REVIEW", "IN_PROGRESS", "WAITING_FOR_CUSTOMER", "COMPLETED", "CANCELLED", "REFUNDED"];

export default function ServiceOrders() {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("ALL");

  const load = () => {
    const p = new URLSearchParams();
    if (status !== "ALL") p.set("service_status", status);
    api.get(`/admin/service-orders?${p}`).then((r) => setRows(r.data.orders)).catch(() => setRows([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const setOrderStatus = async (id, service_status) => {
    try { await api.post(`/admin/service-orders/${id}/status`, { service_status }); toast.success(t("f.save")); load(); }
    catch { toast.error("Error"); }
  };

  const revenue = (rows || []).filter((o) => o.payment_status === "PAID").reduce((s, o) => s + (o.price || 0), 0);

  return (
    <div data-testid="admin-service-orders">
      <PageHeader title={t("im.ordersTitle")} subtitle={t("im.ordersSubtitle")} />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-2" data-testid="orders-revenue"><span className="text-xs text-slate-400">{t("im.paidRevenue")}</span><div className="font-extrabold text-navy">{revenue.toFixed(2)} €</div></div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-56" data-testid="orders-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">{t("im.allStatuses")}</SelectItem>{SERVICE_STATUSES.map((s) => <SelectItem key={s} value={s}>{prettify(s)}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {rows === null ? <PageLoading /> : rows.length === 0 ? <EmptyState icon={ShoppingBag} title={t("im.noOrders")} /> : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="orders-table">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr><th className="px-4 py-3">{t("im.company")}</th><th className="px-4 py-3">{t("im.service")}</th><th className="px-4 py-3">{t("im.amount")}</th><th className="px-4 py-3">{t("im.payment")}</th><th className="px-4 py-3">{t("im.serviceStatus")}</th><th className="px-4 py-3">{t("im.created")}</th></tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.id} className="border-b border-slate-50" data-testid={`order-${o.id}`}>
                  <td className="px-4 py-3 font-semibold text-navy">{o.company_name}</td>
                  <td className="px-4 py-3 text-slate-600">{o.service_label}</td>
                  <td className="px-4 py-3 text-slate-600">{(o.price || 0).toFixed(2)} €</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-bold uppercase ${o.payment_status === "PAID" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>{o.payment_status}</span></td>
                  <td className="px-4 py-3">
                    <Select value={o.service_status} onValueChange={(v) => setOrderStatus(o.id, v)}>
                      <SelectTrigger className="h-8 w-44" data-testid={`order-status-${o.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{SERVICE_STATUSES.map((s) => <SelectItem key={s} value={s}>{prettify(s)}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{fmtDate(o.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
